import { DropdownRecord } from "common/components/Dropdown/Dropdown";
import { EstimateRecord } from "common/records/Estimate";
import { Nullable } from "common/types";
import * as EmailValidator from "email-validator";
import { List } from "immutable";

/**
 * Compose multiple form validators on one field level validation
 *
 * @param {...validators} validators
 * @returns {Nullable<string>}
 */
export const composeValidators =
  (...validators: any[]) =>
  (value: Nullable<string>): Nullable<string> => {
    return validators.reduce((error, validator) => error || validator(value), undefined);
  };

/**
 * Compose multiple form validators and ensure atleast one form validator is valid
 *
 * @param {errorMessage} string
 * @param {...validators} validators
 * @returns {Nullable<string>}
 */
export const ensureOneValidators =
  (errorMessage: Nullable<string>, ...validators: any[]) =>
  (value: Nullable<string>): Nullable<string> => {
    return validators.reduce((error, validator) => validator(value) && error, errorMessage);
  };

/**
 * Returns undefined for valid inputs and the erorr message for invalid inputs.
 *
 * @param {data: Nullable<string | number>} input
 * @returns {Nullable<string>}
 */
export const required = (data: Nullable<string | number>): Nullable<string> => {
  const badValues = [null, undefined, ""];

  const dataStr = typeof data === "number" ? data.toString() : data;
  return badValues.includes(dataStr?.trim()) ? "Required" : undefined;
};

/**
 * Returns undefined for valid inputs and the erorr message for invalid inputs.
 *
 * @param {data: Nullable<string | number>} input
 * @returns {Nullable<string>}
 */
export const dropdownRequired = (data: Nullable<DropdownRecord | List<DropdownRecord>>): Nullable<string> => {
  const badValues = [null, undefined, ""];

  return badValues.includes(data as any) ? "Required" : undefined;
};
/**
 * Returns undefined for valid inputs and the error message for invalid inputs.
 *
 * @param {data: Nullable<List<string>>} input
 * @returns {Nullable<string>}
 */
export const checkboxGroupRequired = (data: Nullable<List<string>>): Nullable<string> => {
  const badValues = [null, undefined];

  // Check if data is null/undefined or an empty List
  if (badValues.includes(data as any) || (data && data.isEmpty())) {
    return "Required";
  }

  return undefined;
};

/**
 * Returns undefined for valid dates and the error message for invalid dates.
 * @param {data: Nullable<Date>} input
 * @returns {Nullable<string>}
 */
export const dateRequired = (data: Nullable<Date>): Nullable<string> => {
  return !data ? "Required" : undefined;
};

/**
 * Returns undefined for valid base url (no query params) and the error message for invalid urls.
 */
export const linkPresentation = (input: Nullable<string>): Nullable<string> => {
  if (!input) return undefined;

  const url_regex = new RegExp(/^(?:(http(s)|ingage)?:\/\/).+/);
  return input.match(url_regex) ? undefined : "Invalid URL";
};

/**
 * Returns undefined for valid phone number and the erorr message for invalid phone numbers.
 *
 * @param {data: Nullable<string>} input
 * @returns {Nullable<string>}
 */
export const phoneNumber = (phone: Nullable<string>): Nullable<string> => {
  if (!phone) return undefined;

  let validPhoneNumber = false;
  if (phone === "") {
    validPhoneNumber = true;
  } else {
    validPhoneNumber = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/.test(phone);
  }
  return validPhoneNumber ? undefined : "Improperly formatted Phone Number. Numeric Only.";
};

/*
 * Returns undefined for valid emails and the error message for invalid emails.
 *
 * @param {data: Nullable<string>} input
 * @returns {Nullable<string>}
 */
export const email = (email: string): Nullable<string> => {
  return EmailValidator.validate(email) ? undefined : "Improperly formatted email '_@__.__'";
};

/**
 * Returns undefined for valid comma separated emails and the erorr message for invalid emails.
 *
 * @param {data: Nullable<string>} input
 * @returns {Nullable<string>}
 */
export const commaSeparatedEmail = (email: Nullable<string>): Nullable<string> => {
  if (!email) return undefined;

  let validEmail = false;
  if (email === "") {
    validEmail = true;
  } else {
    validEmail = true;
    email.split(",").forEach((e) => {
      if (!EmailValidator.validate(e)) validEmail = false;
    });
  }
  return validEmail ? undefined : "Improperly formatted emails '_@__.__' (comma separated)";
};

/**
 * Returns undefined for valid base url (no query params) and the error message for invalid urls.
 */
export const validateBaseUrl = (input: string): Nullable<string> => {
  if (!input) return null;

  const url_regex = new RegExp(
    /^((?:http:\/\/)|(?:https:\/\/))(www.)?((?:[\-a-zA-Z0-9]+\.[\-a-z]{2})|(?:\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}(?::\d+)?))([\/\-a-zA-Z0-9\.]*)$/,
  );
  return input.match(url_regex) ? undefined : "Invalid URL";
};

/**
 * Returns undefined for valid https base url (including query params) and the error message for invalid  or not https urls.
 */
export const validateHttpsBaseUrl = (input: Nullable<string>): Nullable<string> => {
  if (!input) return undefined;

  const url_regex = new RegExp(
    /^((?:https:\/\/))(www\.)?((?:[\w\-]+\.)+(?:[\w\-]{2,})(?:\.[a-z]{2,})?|(?:\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}(?::\d+)?))([\/\w\-\.?#&=\[\]]*)$/,
  );
  return input.match(url_regex) ? undefined : "Invalid URL";
};

/**
 * Parses an http(s) URL with the WHATWG `URL` parser instead of a hand-rolled
 * character allow-list, so query strings, fragments, and percent-encoding are
 * handled correctly. Returns undefined when valid, "Invalid URL" otherwise.
 *
 * @param {string} input - the URL to validate
 * @param {boolean} httpsOnly - when true, only `https:` is accepted
 * @returns {Nullable<string>}
 */
const parseHttpUrl = (input: string, httpsOnly: boolean): Nullable<string> => {
  // The WHATWG `URL` parser silently strips whitespace — both surrounding
  // ("  https://x  ") and internal ("https://exa\tmple.com" → "example.com") —
  // so a value would validate and then save with the whitespace intact. Reject
  // any raw whitespace up front to keep the pre-25842 anchored-regex strictness.
  if (/\s/.test(input)) return "Invalid URL";

  let url: URL;
  try {
    url = new URL(input);
  } catch {
    return "Invalid URL";
  }

  const allowedProtocols = httpsOnly ? ["https:"] : ["http:", "https:"];
  if (!allowedProtocols.includes(url.protocol)) return "Invalid URL";

  // Require a dotted hostname with non-empty labels (or an IP) so bare hosts
  // ("http://google") and empty-label hosts ("http://.com") stay rejected — `URL`
  // itself parses both without throwing.
  if (!/^[^.\s]+(\.[^.\s]+)+$/.test(url.hostname)) return "Invalid URL";

  return undefined;
};

/**
 * Returns undefined for valid http/https URLs (query strings, fragments, and
 * percent-encoding included) and the error message for invalid urls. Empty input
 * is undefined — emptiness is `required`'s job.
 */
export const validateUrl = (input: Nullable<string>): Nullable<string> => {
  if (!input) return undefined;

  return parseHttpUrl(input, false);
};

/**
 * Returns undefined for valid https-only URLs (query strings, fragments, and
 * percent-encoding included) and the error message for invalid or non-https urls.
 * Empty input is undefined — emptiness is `required`'s job.
 */
export const validateHttpsUrl = (input: Nullable<string>): Nullable<string> => {
  if (!input) return undefined;

  return parseHttpUrl(input, true);
};

/**
 * Returns undefined for valid relate urls (including query params) and the error message for invalid urls.
 */
export const validateRelativeUrl = (input: Nullable<string>): Nullable<string> => {
  if (!input) return undefined;

  const url_regex = new RegExp(/^[^\/]*\/[^\/].*$/);
  return input.match(url_regex) ? undefined : "Invalid relative URL";
};

/**
 * Returns undefined for valid password and the error message for invalid passwords.
 */
export const validatePassword = (password: string): Nullable<string> => {
  if (!password.match(/[a-z]/)) {
    return "Password must contain at least one lowercase letter";
  }
  if (!password.match(/[A-Z]/)) {
    return "Password must contain at least one uppercase letter";
  }
  if (!password.match(/[0-9]/)) {
    return "Password must contain at least one number";
  }
  if (!password.match(/[^a-zA-Z0-9]/)) {
    return "Password must contain at least one special character";
  }
  if (password.length < 12) {
    return "Password must be at least 12 characters long";
  }
  if (password !== password.trim()) {
    return "Password cannot have leading or trailing spaces";
  }
  if (password.length > 128) {
    return "Password cannot be more than 128 characters long";
  }
  return undefined;
};

/**
 * Returns undefined for value greater than 0 and error message with 0 or less
 */
export const valueGreaterThanOrEqualToOne = (value: number | string): Nullable<string> => {
  if (typeof value === "string") {
    value = parseFloat(value);
  }
  return value >= 1 ? undefined : "Value must be greater than 1";
};

/**
 * Returns undefined for value greater than 0 and error message for 0 or less
 */
export const valueGreaterThanZero = (value: number): Nullable<string> => {
  return value > 0 ? undefined : "Value must be greater than 0";
};

/**
 * Returns undefined for value greater than 0 and error message for 0 or less
 */
export const valueGreaterThanOrEqualToZero = (value: number | string): Nullable<string> => {
  if (typeof value === "string") {
    value = parseFloat(value);
  }
  return value >= 0 ? undefined : "Value must be greater than or equal to 0";
};

/**
 * Returns invalid date if start time is not before end time
 */
export const validateStartEndDate = (_: any, record: any) => {
  if (record && record.start_time >= record.end_time) {
    return "Start Time must be before End Time";
  }
  return "";
};

/**
 * Validates an estimate's estimated_start against its estimated_end: both-or-neither, and start <= end
 * (equal values accepted). Note this compares the two Date *instants*, so a shared calendar day is only
 * accepted when both values sit at the same instant — the `dateOnly` DatePicker upstream is what makes
 * that true for these fields, and `estimateFromJSON` hydrates them at local midnight.
 *
 * @param _ - the changed field's own value; unused, the verdict comes from the record
 * @param record - the estimate being validated. Optional because a two-argument validator must not
 *   assume the root record is present; `Form` skips this one when the record is absent, so the guard
 *   below is a belt-and-braces contract rather than a live code path.
 * @returns the error message, or an empty string when valid
 */
export function validateEstimatedStartDate(_: any, record?: EstimateRecord): string {
  if (!record) return "";

  const end = record.estimated_end ?? null;
  const start = record.estimated_start ?? null;

  if (end && !start) {
    return "Estimated Start Date is required when Estimated End Date is entered";
  }

  if (start && end && start > end) {
    return "Estimated Start Date must be before or equal to Estimated End Date";
  }

  return "";
}

/**
 * Validates an estimate's estimated_end against its estimated_start: both-or-neither, and end >= start
 * (equal values accepted). Compares the two Date *instants* — see validateEstimatedStartDate for why a
 * shared calendar day only compares equal when the `dateOnly` DatePicker normalized both writes.
 *
 * @param _ - the changed field's own value; unused, the verdict comes from the record
 * @param record - the estimate being validated. Optional because a two-argument validator must not
 *   assume the root record is present; `Form` skips this one when the record is absent, so the guard
 *   below is a belt-and-braces contract rather than a live code path.
 * @returns the error message, or an empty string when valid
 */
export function validateEstimatedEndDate(_: any, record?: EstimateRecord): string {
  if (!record) return "";

  const start = record.estimated_start ?? null;
  const end = record.estimated_end ?? null;

  if (start && !end) {
    return "Estimated End Date is required when Estimated Start Date is entered";
  }

  if (start && end && end < start) {
    return "Estimated End Date must be after or equal to Estimated Start Date";
  }

  return "";
}

/**
 * Returns error message if value contains whitespace or uppercase letters
 */
export const lowerCaseNoWhitespace = (value: string): Nullable<string> => {
  if (!value) return undefined;
  if (/\s/.test(value)) return "Value must not contain whitespace";
  if (value !== value.toLowerCase()) return "Value must be in lowercase";
  return undefined;
};

/**
 * Returns error message if there are unclosed parentheses
 */
export const closedParentheses = (value: string): Nullable<string> => {
  const openParentheses = (value.match(/\(/g) || []).length;
  const closeParentheses = (value.match(/\)/g) || []).length;
  if (openParentheses !== closeParentheses) {
    return "Syntax Error: Unclosed parentheses";
  }
  return undefined;
};
